/**
 * YouTube helpers — centralised id extraction + IFrame Player API loader.
 *
 * We deliberately funnel every embed through the custom `YouTubePlayer`
 * component (see `src/components/media/YouTubePlayer.tsx`) so the app never
 * shows raw YouTube chrome. This module owns the two low-level concerns:
 *   1. Turning any YouTube URL / id into a clean 11-char video id.
 *   2. Loading the (single, shared) IFrame Player API script so we can drive
 *      playback ourselves with `controls=0`.
 */

// ---------------------------------------------------------------------------
// Id extraction
// ---------------------------------------------------------------------------

/**
 * Extract the YouTube video id from a watch / youtu.be / embed / shorts URL,
 * or return the input unchanged if it already looks like a bare id.
 * Returns null when nothing recognizable is found.
 */
export function extractYouTubeId(input?: string | null): string | null {
  if (!input) return null;
  const raw = input.trim();

  // Already a bare id (YouTube ids are 11 chars of [A-Za-z0-9_-]).
  if (/^[\w-]{11}$/.test(raw)) return raw;

  // Try structured URL parsing first (handles query params robustly).
  try {
    const u = new URL(raw);
    const host = u.hostname.replace(/^www\./, '');
    let id = '';
    if (host === 'youtu.be') {
      id = u.pathname.slice(1);
    } else if (
      host === 'youtube.com' ||
      host === 'm.youtube.com' ||
      host === 'youtube-nocookie.com'
    ) {
      if (u.pathname === '/watch') id = u.searchParams.get('v') ?? '';
      else if (u.pathname.startsWith('/embed/')) id = u.pathname.slice('/embed/'.length);
      else if (u.pathname.startsWith('/shorts/')) id = u.pathname.slice('/shorts/'.length);
      else if (u.pathname.startsWith('/live/')) id = u.pathname.slice('/live/'.length);
      // attribution_link wraps the real target (url-encoded) in the `u` param,
      // e.g. /attribution_link?a=…&u=%2Fwatch%3Fv%3D<id>%26… — recurse into it.
      else if (u.pathname === '/attribution_link') {
        const nested = u.searchParams.get('u');
        if (nested) return extractYouTubeId(`https://www.youtube.com${nested}`);
      }
    }
    id = id.split('/')[0].split('?')[0];
    if (/^[\w-]{6,}$/.test(id)) return id;
  } catch {
    // not a URL — fall through to the loose regex below
  }

  // Loose fallback for strings that aren't full URLs.
  const m = raw.match(
    /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/|live\/)|youtu\.be\/)([\w-]{6,})/,
  );
  return m ? m[1] : null;
}

// ---------------------------------------------------------------------------
// IFrame Player API loader (loaded once, shared across every player instance)
// ---------------------------------------------------------------------------

export interface YTPlayer {
  playVideo(): void;
  pauseVideo(): void;
  seekTo(seconds: number, allowSeekAhead: boolean): void;
  getCurrentTime(): number;
  getDuration(): number;
  mute(): void;
  unMute(): void;
  isMuted(): boolean;
  setVolume(volume: number): void;
  getVolume(): number;
  getPlayerState(): number;
  destroy(): void;

  /**
   * Скорость воспроизведения.
   *
   * ⚠️ ЭТА ЧАСТЬ ИНТЕРФЕЙСА, В ОТЛИЧИЕ ОТ СУБТИТРОВ, ДОКУМЕНТИРОВАНА И РАБОТАЕТ
   * НА ЖИВОМ ПЛЕЕРЕ. Пересоздавать его ради смены скорости не нужно.
   */
  setPlaybackRate(rate: number): void;
  getPlaybackRate(): number;
}

export interface YTPlayerEvent {
  target: YTPlayer;
  data: number;
}

interface YTPlayerOptions {
  videoId: string;
  host?: string;
  width?: string | number;
  height?: string | number;
  playerVars?: Record<string, string | number>;
  events?: {
    onReady?: (e: YTPlayerEvent) => void;
    onStateChange?: (e: YTPlayerEvent) => void;
    /** Плеер сообщил об ошибке — ролик удалён, закрыт для встраивания и т.п. */
    onError?: (e: { data: number }) => void;
  };
}

interface YTNamespace {
  Player: new (el: HTMLElement | string, opts: YTPlayerOptions) => YTPlayer;
  PlayerState: {
    UNSTARTED: number;
    ENDED: number;
    PLAYING: number;
    PAUSED: number;
    BUFFERING: number;
    CUED: number;
  };
}

declare global {
  interface Window {
    YT?: YTNamespace;
    onYouTubeIframeAPIReady?: () => void;
  }
}

const API_SRC = 'https://www.youtube.com/iframe_api';
let apiPromise: Promise<YTNamespace> | null = null;

/**
 * Load the YouTube IFrame Player API exactly once and resolve with the `YT`
 * namespace. Guards against double-injecting the script tag and chains any
 * pre-existing `onYouTubeIframeAPIReady` callback so we don't clobber it.
 */
export function loadYouTubeIframeApi(): Promise<YTNamespace> {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return Promise.reject(new Error('YouTube API unavailable (no DOM)'));
  }
  if (window.YT && window.YT.Player) return Promise.resolve(window.YT);
  if (apiPromise) return apiPromise;

  apiPromise = new Promise<YTNamespace>((resolve, reject) => {
    let settled = false;

    // On any failure path we drop the cached promise so a later mount (once
    // the block/proxy/offline condition clears) can retry from scratch.
    const fail = (err: Error) => {
      if (settled) return;
      settled = true;
      apiPromise = null;
      window.clearTimeout(timeoutId);
      reject(err);
    };

    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      prev?.();
      if (settled) return;
      if (window.YT && window.YT.Player) {
        settled = true;
        window.clearTimeout(timeoutId);
        resolve(window.YT);
      }
    };

    // Give up if the API never signals ready (e.g. the script silently
    // fails, or a proxy serves a page that never calls our callback).
    const timeoutId = window.setTimeout(
      () => fail(new Error('YT API load timed out')),
      15_000,
    );

    if (!document.getElementById('youtube-iframe-api')) {
      const tag = document.createElement('script');
      tag.id = 'youtube-iframe-api';
      tag.src = API_SRC;
      // A blocked / offline / proxied request rejects here so the caller's
      // .catch fires instead of hanging on a promise that never settles.
      tag.onerror = () => fail(new Error('YT API failed to load'));
      document.head.appendChild(tag);
    }
  });
  return apiPromise;
}
